import React, { useCallback, useMemo, useRef } from 'react';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import { ContentScoreUtilizationBar } from './ContentScoreUtilizationBar';
import DonutChartLoading from 'src/components/Charts/Donut/DonutChartLoading';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import saveAs from 'file-saver';
import { useTableData } from 'src/serverProxy/useTableData';
import { safeDivide } from 'src/utils/app';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import BeaconComparableSplineChart from 'src/components/BeaconV2Components/BeaconMetricSplineChart/BeaconComparableSplineChart';
import ContentScoreByProductGrid from './ContentScoreByProductGrid';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import CircularProgressPercentage from 'src/components/common/UploadAnimation/CircularProgressPercentage';
import { getWeekLastDay } from 'src/utils/dateformatting';

enum ContentScoreField {
  Title = 'weightedTitleScoreByUnits',
  Bullets = 'weightedBulletScoreByUnits',
  Image = 'weightedImageScoreByUnits',
  Video = 'weightedVideoScoreByUnits',
  APlus = 'weightedAplusScoreByUnits'
}

/**
 * Displays visualizations for the content utilization page
 */
const ContentScorePage = () => {
  const { endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { endWeek: comparisonEndWeek } = useAppSelector((state) => state.comparisonTimePeriod);
  const formatMetric = useMetricFormatter();
  const donutChartRef = useRef<HTMLDivElement>(null);

  const legendLabel = getWeekLastDay(endWeek, true).toString();
  const legendComparisonLabel = getWeekLastDay(comparisonEndWeek, true).toString();

  const { getValuesByFieldName, isLoading, getValueDifferencesByFieldName } = useTableData({
    fields: [
      {
        name: 'unitsSoldForContentScore'
      },
      {
        name: ContentScoreField.APlus
      },
      {
        name: ContentScoreField.Bullets
      },
      {
        name: 'weightedContentScoreByUnits'
      },
      {
        name: ContentScoreField.Image
      },
      {
        name: ContentScoreField.Title
      },
      {
        name: ContentScoreField.Video
      },
      {
        name: 'contentScore'
      }
    ],
    groupByFieldName: 'retailerId',
    indexName: 'new-content-metrics',
    sortFieldName: 'weightedContentScoreByUnits',
    itemsPerPage: 1200,
    pageNumber: 1,
    requestId: 'weightedContentScorePage',
    startWeekId: endWeek,
    customComparisonStartWeekId: comparisonEndWeek
  });

  const getContentValue = useCallback(
    (fieldName: string): number => getValuesByFieldName(fieldName)[0] || 0,
    [getValuesByFieldName]
  );

  const getComparisonContentValue = useCallback(
    (fieldName: string): number => {
      const comparisonDifference = getValueDifferencesByFieldName(fieldName)[0] || 0;
      return getContentValue(fieldName) - comparisonDifference;
    },
    [getContentValue, getValueDifferencesByFieldName]
  );

  const unitsSoldForContentScoreComparison = useMemo(
    () => getComparisonContentValue('unitsSoldForContentScore'),
    [getComparisonContentValue]
  );
  const unitsSoldForContentScore = useMemo(() => getContentValue('unitsSoldForContentScore'), [getContentValue]);

  const contentScore = useMemo(() => getContentValue('contentScore'), [getContentValue]);

  const contentScores = [
    ContentScoreField.Title,
    ContentScoreField.Bullets,
    ContentScoreField.Image,
    ContentScoreField.Video,
    ContentScoreField.APlus
  ].reduce(
    (acc, fieldName) => ({
      ...acc,
      [fieldName]: {
        value: safeDivide(getContentValue(fieldName), unitsSoldForContentScore),
        comparisonValue: safeDivide(getComparisonContentValue(fieldName), unitsSoldForContentScoreComparison)
      }
    }),
    {} as Record<ContentScoreField, { value: number; comparisonValue: number }>
  );

  const data = [
    ['Title Score', contentScores[ContentScoreField.Title].value],
    ['Bullets Score', contentScores[ContentScoreField.Bullets].value],
    ['Image Score', contentScores[ContentScoreField.Image].value],
    ['Video Score', contentScores[ContentScoreField.Video].value],
    ['Enhanced Score', contentScores[ContentScoreField.APlus].value]
  ];

  const comparisonData = [
    ['Title Score', contentScores[ContentScoreField.Title].comparisonValue],
    ['Bullets Score', contentScores[ContentScoreField.Bullets].comparisonValue],
    ['Image Score', contentScores[ContentScoreField.Image].comparisonValue],
    ['Video Score', contentScores[ContentScoreField.Video].comparisonValue],
    ['Enhanced Score', contentScores[ContentScoreField.APlus].comparisonValue]
  ];

  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        <div ref={donutChartRef} style={{ width: '100%', height: '100%' }}>
          <Flex flexDirection="column" gap="xxl">
            <CommonSummaryInfoSubtitle title="Content Utilization" />
            <Flex alignItems="center" gap={108}>
              <div style={{ width: '308px', height: '308px' }}>
                {isLoading ? <DonutChartLoading /> : <CircularProgressPercentage percentage={contentScore} />}
              </div>
              <div style={{ flex: 1 }}>
                {isLoading ? (
                  <SplineChartLoading width={543} />
                ) : (
                  <BeaconChartWithLegend
                    showTitleSummary={false}
                    convertSeriesToDelimitedData={() => {
                      const csvContent = data.map((rowElements) => rowElements.join(',')).join('\n');
                      const blob = new Blob([csvContent], { type: 'text/plain;charset=utf-8' });
                      saveAs(blob, `Content Utilization.csv`);
                      return Promise.resolve();
                    }}
                    primaryLegendProps={{
                      displayName: legendLabel
                    }}
                    comparisonLegendProps={{
                      displayName: legendComparisonLabel
                    }}
                    title="Content Utilization"
                    saveImageRef={donutChartRef}
                  >
                    <TopEntityChartV2
                      chartSeries={[
                        {
                          type: 'column',
                          data: comparisonData,
                          metricType: METRICTYPE.PERCENT
                        },
                        {
                          type: 'column',
                          data,
                          metricType: METRICTYPE.PERCENT
                        }
                      ]}
                      chartOptionsOverride={(defaults) => ({
                        ...defaults,
                        plotOptions: {
                          ...defaults.plotOptions,
                          series: {
                            ...defaults?.series?.plotOptions,
                            dataLabels: {
                              ...defaults.plotOptions?.series?.dataLabels,
                              formatter() {
                                return `${formatMetric(this.y, METRICTYPE.PERCENT, {
                                  decimalPlaces: 1,
                                  showFullValue: false
                                })}`;
                              }
                            }
                          }
                        },
                        chart: {
                          ...defaults.chart,
                          height: 240,
                          spacingTop: 15,
                          marginBottom: 39,
                          spacingBottom: 0
                        }
                      })}
                      leftAlignBars={false}
                    />
                  </BeaconChartWithLegend>
                )}
              </div>
            </Flex>
          </Flex>
        </div>
        <BeaconComparableSplineChart indexName="new-content-metrics" fieldName="contentScore" />
        <ContentScoreUtilizationBar />
        <ContentScoreByProductGrid />
      </Flex>
    </BasePageContainer>
  );
};

export default ContentScorePage;
